// The roll-up scorecard (FOUNDER-OS phase 1, design §2.4): KPIs › Scorecard in a portfolio
// company. One row per company from its `kpi` projections (`GET portfolio/scorecard`): health
// dot, score, trend, completeness and the last 13 weeks; a row expands into its CEO seat's and
// the company's metrics (value, target, hit or miss) as the company shares them ("Scores only",
// "Withheld by <company>" otherwise), each with "Open in <company>" (its own scorecard, through
// the viewer's own standing there). The company name opens its company page. `?company=` (the
// KPIs scope tree) opens and scrolls to that row. The portfolio's own seats keep the ordinary
// grid, one link away (`?board=own`).
import { Badge, Button, cn, LocationHeader } from "@companyos/ui";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { ChevronRightIcon, ExternalLinkIcon, Rows3Icon } from "lucide-react";
import { useEffect, useState } from "react";
import type { Api } from "../../api/client";
import type { PortfolioMetric, PortfolioScorecardRow, PortfolioWeek } from "../../api/types";
import { HealthDot, SwitcherTile } from "../../shell/company-switcher";
import type { CompanyAccess } from "../company";
import { formatCardMetric, formatPercent, formatScore } from "../format";
import { LoadError, Loading, Nothing } from "../screen-parts";
import { TrendArrow, Withheld } from "./portfolio-parts";
import { portfolioScorecardQuery } from "./portfolio-queries";

/** "2026-W43" → "W43". */
const weekLabel = (key: string) => (key.includes("-W") ? `W${key.split("-W")[1]}` : key);

export function PortfolioScorecardScreen({
  api,
  company,
  focusCompany,
}: {
  api: Api;
  company: CompanyAccess;
  /** `?company=`: the row to open and scroll to. */
  focusCompany?: string | undefined;
}) {
  const card = useQuery(portfolioScorecardQuery(api, company.id));
  const [open, setOpen] = useState<Set<string>>(() => new Set(focusCompany ? [focusCompany] : []));
  useEffect(() => {
    if (!focusCompany || !card.data) return;
    setOpen((s) => new Set(s).add(focusCompany));
    document
      .querySelector(`[data-company-id="${focusCompany}"]`)
      ?.scrollIntoView?.({ block: "nearest" });
  }, [focusCompany, card.data]);
  const toggle = (id: string) =>
    setOpen((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const weeks = card.data?.weeks ?? [];
  return (
    <div className="flex min-w-0 flex-1 flex-col" data-slot="portfolio-scorecard">
      <LocationHeader
        icon={Rows3Icon}
        crumbs={[
          { label: "KPIs", render: <Link to="/c/$slug/kpis" params={{ slug: company.slug }} /> },
        ]}
        title="Scorecard"
        {...(card.data ? { count: card.data.companies.length } : {})}
      />
      <div className="flex min-w-0 flex-col gap-6 px-6 py-5 max-md:px-4">
        {card.isPending ? (
          <Loading label="Loading the roll-up scorecard" rows={4} />
        ) : card.isError ? (
          <LoadError what="The roll-up scorecard" error={card.error} />
        ) : card.data.companies.length === 0 ? (
          <Nothing title="No companies yet">
            Add a company from Portfolio › Focus; its scores show here as they arrive.
          </Nothing>
        ) : (
          <div className="overflow-x-auto rounded-md border border-border">
            <table className="w-full min-w-max border-collapse text-sm" aria-label="Companies">
              <thead className="bg-sunken text-xs text-muted-foreground">
                <tr>
                  <th scope="col" className="px-3 py-2 text-left font-medium">
                    Company
                  </th>
                  <th scope="col" className="px-3 py-2 text-left font-medium">
                    Score
                  </th>
                  <th scope="col" className="px-3 py-2 text-left font-medium">
                    Complete
                  </th>
                  {weeks.map((w) => (
                    <th key={w} scope="col" className="px-2 py-2 text-center font-medium">
                      {weekLabel(w)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {card.data.companies.map((r) => (
                  <CompanyRows
                    key={r.company_id}
                    slug={company.slug}
                    row={r}
                    weeks={weeks.length}
                    open={open.has(r.company_id)}
                    onToggle={() => toggle(r.company_id)}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
        <section aria-labelledby="own-seats" className="flex flex-col gap-1">
          <h2 id="own-seats" className="font-heading text-base font-semibold">
            Your own seats
          </h2>
          <p className="text-sm text-muted-foreground">
            The founder, EA and chief of staff seats keep the ordinary scorecard.{" "}
            <Link
              to="/c/$slug/kpis/scorecard"
              params={{ slug: company.slug }}
              search={{ board: "own" } as never}
              className="font-medium text-primary-text hover:underline"
            >
              Open {company.name}'s scorecard
            </Link>
          </p>
        </section>
      </div>
    </div>
  );
}

function CompanyRows({
  slug,
  row,
  weeks,
  open,
  onToggle,
}: {
  slug: string;
  row: PortfolioScorecardRow;
  weeks: number;
  open: boolean;
  onToggle: () => void;
}) {
  const span = 3 + weeks;
  const withheld = row.sharing === "withheld";
  return (
    <>
      <tr
        data-company={row.slug}
        data-company-id={row.company_id}
        data-open={open || undefined}
        className="border-t border-subtle"
      >
        <td className="px-3 py-2">
          <span className="flex min-w-0 items-center gap-1.5">
            <Button
              variant="ghost"
              size="icon-xs"
              aria-expanded={open}
              aria-label={`${open ? "Collapse" : "Expand"} ${row.name}`}
              onClick={onToggle}
            >
              <ChevronRightIcon
                aria-hidden="true"
                className={cn("transition-transform", open && "rotate-90")}
              />
            </Button>
            <Link
              to="/c/$slug/portfolio/companies/$companyId"
              params={{ slug, companyId: row.company_id }}
              className="inline-flex min-w-0 items-center gap-2 font-medium text-foreground hover:underline"
            >
              <SwitcherTile name={row.name} portfolio={false} />
              <span className="truncate">{row.name}</span>
            </Link>
          </span>
        </td>
        <td className="px-3 py-2">
          {withheld ? (
            <Withheld name={row.name} />
          ) : row.kpi_score === null && row.band === null ? (
            <span className="text-muted-foreground">No numbers yet</span>
          ) : (
            <span className="inline-flex items-center gap-1.5 tabular-nums">
              <HealthDot band={row.band} />
              {formatScore(row.kpi_score)}
              <TrendArrow trend={row.trend} />
            </span>
          )}
        </td>
        <td className="px-3 py-2 tabular-nums text-secondary">
          {row.completeness === null ? "—" : formatPercent(row.completeness)}
        </td>
        {Array.from({ length: weeks }, (_, i) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: the cells are the grid's week columns
          <WeekCell key={i} week={row.weeks[i] ?? null} />
        ))}
      </tr>
      {open ? (
        <tr data-slot="company-metrics" className="bg-sunken/50">
          <td colSpan={span} className="px-3 py-2">
            {withheld ? (
              <Withheld name={row.name} />
            ) : row.sharing === "scores" ? (
              <span className="text-xs text-muted-foreground">
                Scores only: {row.name} does not share metric values.
              </span>
            ) : (
              <div className="flex flex-col gap-3">
                <MetricList
                  title={row.head_seat ? `CEO seat · ${row.head_seat.title}` : "CEO seat"}
                  metrics={row.head_metrics}
                  row={row}
                />
                <MetricList title="Company" metrics={row.company_metrics} row={row} />
              </div>
            )}
          </td>
        </tr>
      ) : null}
    </>
  );
}

function WeekCell({ week }: { week: PortfolioWeek | null }) {
  if (!week)
    return (
      <td className="px-2 py-2 text-center">
        <span
          role="img"
          aria-label="No numbers"
          className="inline-block size-1.5 rounded-full bg-border"
        />
      </td>
    );
  if (week.withheld)
    return (
      <td className="px-2 py-2 text-center text-xs text-faint" title="Withheld">
        —
      </td>
    );
  return (
    <td className="px-2 py-2 text-center" data-band={week.band ?? undefined}>
      <span className="inline-flex items-center gap-1 text-xs tabular-nums">
        <HealthDot band={week.band} />
        {week.kpi_score === null ? "" : Math.round(week.kpi_score)}
      </span>
    </td>
  );
}

function MetricList({
  title,
  metrics,
  row,
}: {
  title: string;
  metrics: PortfolioMetric[];
  row: PortfolioScorecardRow;
}) {
  return (
    <div className="flex flex-col gap-1" data-slot="metric-list">
      <h3 className="text-2xs font-medium tracking-wide text-faint uppercase">{title}</h3>
      {metrics.length === 0 ? (
        <p className="text-xs text-muted-foreground">No numbers yet</p>
      ) : (
        <ul className="flex flex-col divide-y divide-border-subtle">
          {metrics.map((m) => (
            <li
              key={m.key}
              data-metric={m.key}
              className="flex flex-wrap items-center gap-3 py-1 text-xs"
            >
              <span className="min-w-40 flex-1 truncate text-secondary">{m.name}</span>
              <span className="w-20 text-right tabular-nums text-foreground">
                {formatCardMetric(m.value, m.unit as never)}
              </span>
              <span className="w-24 text-muted-foreground tabular-nums">
                {m.target === null
                  ? "No target"
                  : `of ${formatCardMetric(m.target, m.unit as never)}`}
              </span>
              {m.hit === null ? (
                <span className="w-12" />
              ) : (
                <Badge variant={m.hit ? "success" : "danger"} className="w-12 justify-center">
                  {m.hit ? "Hit" : "Miss"}
                </Badge>
              )}
              <Link
                to="/c/$slug/kpis/scorecard"
                params={{ slug: row.slug }}
                className="inline-flex items-center gap-1 text-primary-text hover:underline"
              >
                Open in {row.name}
                <ExternalLinkIcon aria-hidden="true" className="size-3" />
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
